// Helcim's card popup.
//
// start.js (loaded in index.html) puts two functions on the page:
// appendHelcimPayIframe(token) opens the popup, removeHelcimPayIframe() takes
// it away. The popup reports back through window messages named after the
// checkout token it was opened with.
//
// Nothing about the payment is decided here: the popup takes the card, and
// POST /billing/checkout/complete is what makes the plan active.

const eventNameFor = (checkoutToken) => `helcim-pay-js-${checkoutToken}`;

/**
 * The popup sends its result as `eventMessage`, which arrives in more than one
 * shape: sometimes an object, sometimes a JSON string of one, and sometimes
 * wrapped one level deeper under `data`. This digs out the { data, hash } the
 * complete call needs, whichever of those it is.
 */
export const readHelcimResult = (eventMessage) => {
  let message = eventMessage;

  if (typeof message === "string") {
    try {
      message = JSON.parse(message);
    } catch {
      return null;
    }
  }

  const payload = message?.data?.data ? message.data : message;

  if (!payload?.data) return null;

  return { data: payload.data, hash: payload.hash };
};

/**
 * Opens the popup and reports what happens.
 *
 * @param {object} options
 * @param {string} options.checkoutToken from POST /billing/checkout, the retry
 *   endpoint, or the guest registration
 * @param {Function} options.onSuccess   ({data, hash}) - the card went through;
 *   the caller completes the checkout with these
 * @param {Function} [options.onDeclined] (message) - the card was refused. The
 *   popup stays open so another card can be tried, so this is a message to
 *   show rather than an end to the flow
 * @param {Function} [options.onClosed]  the visitor closed the popup without
 *   paying
 * @returns {Function} stops listening and takes the popup away - call it when
 *   the component unmounts, or once the payment is finished with
 */
export const openHelcimCheckout = ({
  checkoutToken,
  onSuccess,
  onDeclined,
  onClosed,
}) => {
  // Helcim refuses to frame itself on localhost, so a dev testing a payment
  // needs the app on a public https address (an ngrok tunnel, say). The popup
  // simply never appears otherwise, which is a confusing half-hour.
  if (
    process.env.NODE_ENV === "development" &&
    ["localhost", "127.0.0.1"].includes(window.location.hostname)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      "[helcim] The card form does not open on localhost. Serve the app over a public https address to test a payment.",
    );
  }

  if (!checkoutToken || typeof window.appendHelcimPayIframe !== "function") {
    // The script did not load - a blocked CDN, or an offline tab. Saying so is
    // better than a button that quietly does nothing.
    onDeclined?.(
      "We couldn't open the card form. Check your connection and try again.",
    );
    return () => {};
  }

  const expected = eventNameFor(checkoutToken);

  const close = () => {
    window.removeEventListener("message", onMessage);
    if (typeof window.removeHelcimPayIframe === "function") {
      window.removeHelcimPayIframe();
    }
  };

  function onMessage(event) {
    // Every popup names its messages after its own token, so two checkouts
    // opened in one session cannot be confused for each other.
    if (event.data?.eventName !== expected) return;

    if (event.data.eventStatus === "SUCCESS") {
      const result = readHelcimResult(event.data.eventMessage);

      if (!result) {
        onDeclined?.("The payment came back in a form we couldn't read.");
        return;
      }

      onSuccess?.(result);
      return;
    }

    if (event.data.eventStatus === "ABORTED") {
      // Declined, not finished: the popup stays up for another card.
      onDeclined?.(
        event.data.eventMessage ||
          "That card was declined. Try another card.",
      );
      return;
    }

    if (event.data.eventStatus === "HIDE") {
      close();
      onClosed?.();
    }
  }

  window.addEventListener("message", onMessage);
  window.appendHelcimPayIframe(checkoutToken);

  return close;
};
